import { useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { getIdleConfig, sessionChannel, broadcastSession, onSessionStorage } from "@/lib/session";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ShieldAlert } from "lucide-react";

// Événements humains qui réinitialisent le timer d'inactivité.
// Les appels API automatiques (polling GPS, rafraîchissement carte) NE réinitialisent PAS.
const HUMAN_EVENTS = ["mousedown", "click", "keydown", "touchstart"];

export default function SessionGuard() {
  const { user, logout } = useAuth();
  const [warnOpen, setWarnOpen] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const lastActivityRef = useRef(Date.now());
  const lastBroadcastRef = useRef(0);
  const loggingOutRef = useRef(false);

  const bump = useCallback((broadcast = true) => {
    const now = Date.now();
    lastActivityRef.current = now;
    setWarnOpen(false);
    if (broadcast && now - lastBroadcastRef.current > 5000) {
      lastBroadcastRef.current = now;
      broadcastSession({ type: "activity", ts: now });
    }
  }, []);

  const doLogout = useCallback((reason) => {
    if (loggingOutRef.current) return;
    loggingOutRef.current = true;
    logout(reason);
  }, [logout]);

  // 1) Écoute de l'activité humaine (mousemove + scroll throttlés)
  useEffect(() => {
    if (!user) return;
    loggingOutRef.current = false;
    lastActivityRef.current = Date.now();
    const onActivity = () => bump(true);
    let moveThrottle = 0;
    const onThrottled = () => {
      const now = Date.now();
      if (now - moveThrottle > 1000) { moveThrottle = now; bump(true); }
    };
    HUMAN_EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    window.addEventListener("mousemove", onThrottled, { passive: true });
    window.addEventListener("scroll", onThrottled, { passive: true, capture: true });
    return () => {
      HUMAN_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
      window.removeEventListener("mousemove", onThrottled);
      window.removeEventListener("scroll", onThrottled, { capture: true });
    };
  }, [user, bump]);

  // 2) Synchronisation multi-onglets (activité d'un autre onglet = session active)
  //    + déconnexion propre sur 401 backend (Session expiree)
  useEffect(() => {
    if (!user) return;
    const onMsg = (ev) => {
      const d = ev.data;
      if (d && d.type === "activity" && typeof d.ts === "number") {
        if (d.ts > lastActivityRef.current) lastActivityRef.current = d.ts;
        setWarnOpen(false);
      }
    };
    const ch = sessionChannel();
    if (ch) ch.addEventListener("message", onMsg);
    const offStorage = onSessionStorage(onMsg);
    const onUnauth = () => doLogout("expired");
    window.addEventListener("auth:unauthorized", onUnauth);
    return () => {
      if (ch) ch.removeEventListener("message", onMsg);
      offStorage();
      window.removeEventListener("auth:unauthorized", onUnauth);
    };
  }, [user, doLogout]);

  // 3) Vérification par horloge murale (robuste après mise en veille du PC/mobile)
  useEffect(() => {
    if (!user) return;
    const { idleMs, warnMs } = getIdleConfig();
    const tick = () => {
      const elapsed = Date.now() - lastActivityRef.current;
      if (elapsed >= idleMs) { doLogout("inactivity"); return; }
      if (elapsed >= idleMs - warnMs) {
        setWarnOpen(true);
        setRemaining(Math.max(0, Math.ceil((idleMs - elapsed) / 1000)));
      } else {
        setWarnOpen((o) => (o ? false : o));
      }
    };
    const id = setInterval(tick, 1000);
    const onVisible = () => { if (document.visibilityState === "visible") tick(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    tick();
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [user, doLogout]);

  if (!user) return null;

  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");

  return (
    <Dialog open={warnOpen} onOpenChange={() => { /* choix explicite requis */ }}>
      <DialogContent className="sm:max-w-md" data-testid="session-timeout-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-[#F5821F]" /> Session bientôt expirée
          </DialogTitle>
          <DialogDescription data-testid="session-timeout-message">
            Votre session va expirer dans 2 minutes pour des raisons de sécurité.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2 text-center">
          <span className="text-3xl font-bold tabular-nums text-foreground" data-testid="session-timeout-countdown">
            {mm}:{ss}
          </span>
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" data-testid="session-logout-btn" onClick={() => doLogout(null)}>
            Se déconnecter
          </Button>
          <Button className="bg-[#17A2B8] hover:bg-[#1391a5] text-white" data-testid="session-stay-btn" onClick={() => bump(true)}>
            Rester connecté
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
